import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  OPERATION_TYPES, PRIORITIES, VEHICLE_CATEGORIES, NEEDS, REQUEST_STATUS,
  MISSING_LABELS, fmtDateTime, AZIENDE,
} from "../lib/constants";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Plus, Search, Copy, Trash2, Pencil, PackageCheck, AlertTriangle, Building2, Phone, MapPin,
  ChevronDown, ChevronUp, SlidersHorizontal,
} from "lucide-react";

const EMPTY = {
  azienda: "Ludovica Mascheroni", commessa: "", operation_type: "ritiro",
  supplier_id: "", supplier_location_id: "", origin_address: "", origin_lat: null, origin_lng: null,
  dest_address: "", dest_lat: null, dest_lng: null, contact_name: "", contact_phone: "",
  available_from: "", deadline: "", deadline_strict: false, appointment: "", opening_hours: "",
  priority: "ordinaria", goods_description: "", packages_count: "", dimensions: "",
  weight_kg: "", volume_m3: "", vehicle_required: "", needs: [], load_time_min: "",
  notes: "", linked_request_id: "",
};

export default function Requests() {
  const { user } = useAuth();
  const canWrite = ["pm_produzione", "responsabile_logistica", "amministratore"].includes(user?.role);
  const isManager = ["responsabile_logistica", "amministratore"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "", priority: "", operation_type: "", requester: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1" && canWrite) {
      setEditing({ ...EMPTY }); setOpen(true); setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
    const { data } = await api.get(`/requests?${p.toString()}`);
    setRows(data);
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  const loadSuppliers = useCallback(async () => {
    const { data } = await api.get("/suppliers");
    setSuppliers(data);
    return data;
  }, []);
  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => {
    if (!isManager) return;
    api.get("/users").then((r) => setRequesters(r.data.filter((u) => u.role !== "autista"))).catch(() => {});
  }, [isManager]);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (r) => {
    setEditing({ ...EMPTY, ...r, packages_count: r.packages_count ?? "", weight_kg: r.weight_kg ?? "",
      volume_m3: r.volume_m3 ?? "", load_time_min: r.load_time_min ?? "",
      available_from: (r.available_from || "").slice(0, 16), deadline: (r.deadline || "").slice(0, 16),
      appointment: (r.appointment || "").slice(0, 16), needs: r.needs || [] });
    setOpen(true);
  };

  const save = async (override = {}) => {
    const body = { ...editing, ...override };
    ["packages_count", "load_time_min"].forEach((k) => { body[k] = body[k] === "" ? null : parseInt(body[k]); });
    ["weight_kg", "volume_m3"].forEach((k) => { body[k] = body[k] === "" ? null : parseFloat(body[k]); });
    ["available_from", "deadline", "appointment"].forEach((k) => { body[k] = body[k] ? new Date(body[k]).toISOString() : null; });
    if (!body.supplier_id) body.supplier_id = null;
    if (!body.supplier_location_id) body.supplier_location_id = null;
    try {
      if (editing.id) { await api.put(`/requests/${editing.id}`, body); toast.success("Richiesta aggiornata"); }
      else { await api.post("/requests", body); toast.success("Richiesta creata"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDup = async (r) => { try { await api.post(`/requests/${r.id}/duplicate`); toast.success("Richiesta duplicata"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doDel = async (r) => { if (!window.confirm(`Eliminare ${r.code}?`)) return; try { await api.delete(`/requests/${r.id}`); toast.success("Eliminata"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doReady = async (r) => { try { await api.patch(`/requests/${r.id}/goods-ready`); toast.success("Merce contrassegnata pronta"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-lm-text">Richieste</h1>
          <p className="text-sm text-lm-muted" data-testid="requests-count">
            {rows.length} {!isManager
              ? "richieste (le mie)"
              : filters.requester
                ? `richieste (filtro: ${requesters.find((u) => u.id === filters.requester)?.name || "richiedente"})`
                : "richieste (tutte)"}
          </p>
        </div>
        {canWrite && (
          <Button data-testid="new-request-btn" onClick={openNew} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">
            <Plus className="w-4 h-4 mr-2" /> Nuova richiesta
          </Button>
        )}
      </div>

      <div className={`lm-card p-4 grid sm:grid-cols-2 ${isManager ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`}>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-lm-muted" />
          <Input data-testid="request-search" placeholder="Cerca codice, azienda, merce…" value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-9 bg-white border-lm-sandsoft" />
        </div>
        <FilterSelect testid="filter-status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} placeholder="Tutti gli stati" options={REQUEST_STATUS} />
        <FilterSelect testid="filter-priority" value={filters.priority} onChange={(v) => setFilters((f) => ({ ...f, priority: v }))} placeholder="Tutte le priorità" options={PRIORITIES} />
        <FilterSelect testid="filter-optype" value={filters.operation_type} onChange={(v) => setFilters((f) => ({ ...f, operation_type: v }))} placeholder="Tutti i tipi" options={OPERATION_TYPES} />
        {isManager && (
          <FilterSelect testid="filter-requester" value={filters.requester} onChange={(v) => setFilters((f) => ({ ...f, requester: v }))}
            placeholder="Tutti i richiedenti" options={Object.fromEntries(requesters.map((u) => [u.id, u.name]))} />
        )}
      </div>

      <div className="grid gap-3">
        {rows.length === 0 && <div className="lm-card p-10 text-center text-lm-muted">Nessuna richiesta trovata.</div>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`request-row-${r.code}`} className="lm-card p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-lm-brown">{r.code}</span>
                  <span className="text-xs px-2 py-0.5 rounded-md bg-lm-sandsoft text-lm-brown font-semibold">{OPERATION_TYPES[r.operation_type]}</span>
                  <PriorityBadge priority={r.priority} />
                  <StatusBadge status={r.status} testid={`status-${r.code}`} />
                  {r.deadline_strict && <span className="text-xs px-2 py-0.5 rounded-md bg-[#FDF2F2] text-[#991B1B] border border-[#F8B4B4] font-semibold">Scadenza inderogabile</span>}
                  {r.appointment && <span className="text-xs px-2 py-0.5 rounded-md bg-[#EFE9F6] text-[#5B3E86] border border-[#CDBCE6] font-semibold">Appuntamento</span>}
                  {r.goods_ready && <span className="text-xs px-2 py-0.5 rounded-md bg-[#EFF7F2] text-[#166534] border border-[#9AD4AC] font-semibold">Merce pronta</span>}
                </div>
                <p className="mt-2 text-sm text-lm-text font-medium">{r.supplier_name || r.origin_address || r.dest_address || "—"}</p>
                <p className="text-sm text-lm-text2 truncate">{r.goods_description || "Merce non descritta"}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-lm-muted">
                  <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {r.azienda}{r.commessa ? ` · ${r.commessa}` : ""}</span>
                  <span>Richiedente: {r.requester_name || "—"}</span>
                  <span>Scadenza: {fmtDateTime(r.deadline)}</span>
                  {r.vehicle_required && <span>Mezzo: {r.vehicle_required}</span>}
                </div>
                {r.missing_info?.length > 0 && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-[#9A6B00] bg-[#FEF8EC] border border-[#F3D295] rounded-lg px-2.5 py-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Manca: {r.missing_info.map((m) => MISSING_LABELS[m] || m).join(", ")}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {!r.goods_ready && <IconBtn testid={`ready-${r.code}`} title="Merce pronta" onClick={() => doReady(r)}><PackageCheck className="w-4 h-4" /></IconBtn>}
                {canWrite && <IconBtn testid={`edit-${r.code}`} title="Modifica" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></IconBtn>}
                {canWrite && <IconBtn testid={`dup-${r.code}`} title="Duplica" onClick={() => doDup(r)}><Copy className="w-4 h-4" /></IconBtn>}
                {canWrite && <IconBtn testid={`del-${r.code}`} title="Elimina" onClick={() => doDel(r)}><Trash2 className="w-4 h-4 text-[#991B1B]" /></IconBtn>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && <RequestDialog open={open} setOpen={setOpen} editing={editing} setEditing={setEditing} suppliers={suppliers} reloadSuppliers={loadSuppliers} rows={rows} onSave={save} />}
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger data-testid={testid} className="bg-white border-lm-sandsoft"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}</SelectItem>
        {Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return <button data-testid={testid} title={title} onClick={onClick} className="w-9 h-9 rounded-lg border border-lm-sandsoft bg-white hover:bg-lm-sandsoft flex items-center justify-center text-lm-brown transition-colors">{children}</button>;
}

function RequestDialog({ open, setOpen, editing, setEditing, suppliers, reloadSuppliers, rows, onSave }) {
  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const [dupWarn, setDupWarn] = useState([]);
  const [showAdv, setShowAdv] = useState(false);
  const [qa, setQa] = useState(null);
  const [supplierQuery, setSupplierQuery] = useState(() => (suppliers.find((s) => s.id === editing.supplier_id)?.name) || "");
  const selSup = suppliers.find((s) => s.id === editing.supplier_id);

  const applyLocation = (loc, supId) => {
    const upd = { supplier_id: supId, supplier_location_id: loc.id,
      contact_name: loc.contact_name || editing.contact_name, contact_phone: loc.contact_phone || editing.contact_phone,
      opening_hours: loc.opening_hours || editing.opening_hours };
    if (editing.operation_type === "consegna") { upd.dest_address = loc.address; upd.dest_lat = loc.lat; upd.dest_lng = loc.lng; }
    else { upd.origin_address = loc.address; upd.origin_lat = loc.lat; upd.origin_lng = loc.lng; }
    setEditing((e) => ({ ...e, ...upd }));
  };

  const openQuick = () => setQa({
    name: "", kind: editing.operation_type === "consegna" ? "cliente" : "fornitore",
    address: editing.operation_type === "consegna" ? (editing.dest_address || "") : (editing.origin_address || ""),
    lat: "", lng: "", contact_name: editing.contact_name || "", contact_phone: editing.contact_phone || "",
  });

  const saveQuick = async () => {
    if (!qa.name.trim() || !qa.address.trim()) { toast.error("Nome e indirizzo sono obbligatori"); return; }
    const loc = { id: crypto.randomUUID(), label: "Sede principale", address: qa.address,
      lat: qa.lat === "" ? null : parseFloat(qa.lat), lng: qa.lng === "" ? null : parseFloat(qa.lng),
      contact_name: qa.contact_name, contact_phone: qa.contact_phone, opening_hours: "", access_notes: "" };
    try {
      const { data } = await api.post("/suppliers", { name: qa.name.trim(), kind: qa.kind, notes: "", locations: [loc] });
      await reloadSuppliers();
      applyLocation(loc, data.id);
      setSupplierQuery(qa.name.trim());
      setQa(null);
      toast.success("Salvato in rubrica — ora è disponibile nella tendina");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pickLocation = (locId) => {
    const loc = selSup?.locations?.find((l) => l.id === locId);
    if (!loc) return;
    const upd = { supplier_location_id: locId, contact_name: loc.contact_name || editing.contact_name, contact_phone: loc.contact_phone || editing.contact_phone, opening_hours: loc.opening_hours || editing.opening_hours };
    if (editing.operation_type === "consegna") { upd.dest_address = loc.address; upd.dest_lat = loc.lat; upd.dest_lng = loc.lng; }
    else { upd.origin_address = loc.address; upd.origin_lat = loc.lat; upd.origin_lng = loc.lng; }
    setEditing((e) => ({ ...e, ...upd }));
  };

  const onSupplierType = (val) => {
    setSupplierQuery(val);
    const match = suppliers.find((s) => s.name.trim().toLowerCase() === val.trim().toLowerCase());
    if (match) {
      const locs = match.locations || [];
      if (locs.length === 1) applyLocation(locs[0], match.id);
      else setEditing((e) => ({ ...e, supplier_id: match.id, supplier_location_id: "" }));
    } else if (editing.supplier_id) {
      setEditing((e) => ({ ...e, supplier_id: "", supplier_location_id: "" }));
    }
  };

  const toggleNeed = (n) => set("needs", editing.needs.includes(n) ? editing.needs.filter((x) => x !== n) : [...editing.needs, n]);

  const handleSave = async () => {
    const typed = supplierQuery.trim();
    const exists = suppliers.find((s) => s.name.trim().toLowerCase() === typed.toLowerCase());
    const isDelivery = editing.operation_type === "consegna";
    const addr = isDelivery ? editing.dest_address : editing.origin_address;
    if (typed && !exists && addr && !editing.supplier_id) {
      try {
        const loc = { id: crypto.randomUUID(), label: "Sede principale", address: addr,
          lat: (isDelivery ? editing.dest_lat : editing.origin_lat) ?? null,
          lng: (isDelivery ? editing.dest_lng : editing.origin_lng) ?? null,
          contact_name: editing.contact_name || "", contact_phone: editing.contact_phone || "",
          opening_hours: editing.opening_hours || "", access_notes: "" };
        const { data } = await api.post("/suppliers", { name: typed, kind: isDelivery ? "cliente" : "fornitore", notes: "", locations: [loc] });
        await reloadSuppliers();
        setEditing((e) => ({ ...e, supplier_id: data.id, supplier_location_id: loc.id }));
        toast.success(`"${typed}" aggiunto alla rubrica fornitori`);
        onSave({ supplier_id: data.id, supplier_location_id: loc.id });
        return;
      } catch (e) { /* se fallisce, salva comunque la richiesta */ }
    }
    onSave();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-lm-cream" data-testid="request-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-lm-brown">{editing.id ? `Modifica ${editing.code}` : "Nuova richiesta"}</DialogTitle>
          <DialogDescription className="text-lm-muted text-sm">Compila i campi principali; i dettagli avanzati sono opzionali.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Section title="Riferimenti">
            <Grid>
              <Field label="Azienda di riferimento">
                <Select value={editing.azienda} onValueChange={(v) => set("azienda", v)}>
                  <SelectTrigger data-testid="f-azienda" className="bg-white"><SelectValue placeholder="Seleziona azienda" /></SelectTrigger>
                  <SelectContent>{AZIENDE.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Commessa">
                <Input data-testid="f-commessa" value={editing.commessa} onChange={(e) => set("commessa", e.target.value)} className="bg-white" placeholder="Es. COM-2201" /></Field>
              <Field label="Tipo operazione">
                <Select value={editing.operation_type} onValueChange={(v) => set("operation_type", v)}>
                  <SelectTrigger data-testid="f-optype" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(OPERATION_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </Grid>
          </Section>

          <Section title="Fornitore / Destinazione">
            <Grid>
              <Field label="Fornitore / destinazione" hint="Scrivi il nome: se è già in rubrica, indirizzo e contatti si compilano da soli. Se è NUOVO, viene aggiunto automaticamente alla rubrica quando salvi la richiesta (oppure salvalo subito col pulsante qui sotto).">
                <Input list="lm-supplier-names" data-testid="f-supplier" value={supplierQuery}
                  onChange={(e) => onSupplierType(e.target.value)} placeholder="Scrivi o scegli il fornitore…" className="bg-white" />
                <datalist id="lm-supplier-names">
                  {suppliers.map((s) => <option key={s.id} value={s.name} />)}
                </datalist>
                {supplierQuery.trim() && !suppliers.find((s) => s.name.trim().toLowerCase() === supplierQuery.trim().toLowerCase()) && (
                  <p className="text-[11px] text-lm-terra mt-1 font-medium">Nuovo fornitore: verrà aggiunto alla rubrica al salvataggio.</p>
                )}
                {!qa && (
                  <button type="button" data-testid="quick-add-supplier" onClick={openQuick}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-lm-terra hover:underline">
                    <Plus className="w-3.5 h-3.5" /> Aggiungi subito il fornitore in rubrica
                  </button>
                )}
              </Field>
              {selSup && (
                <Field label="Sede fornitore" hint="Scegli la sede effettiva: l'indirizzo e i contatti verranno compilati da soli.">
                  <Select value={editing.supplier_location_id || "__none"} onValueChange={(v) => v !== "__none" && pickLocation(v)}>
                    <SelectTrigger data-testid="f-location" className="bg-white"><SelectValue placeholder="Seleziona sede" /></SelectTrigger>
                    <SelectContent>{(selSup.locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.label} — {l.address}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
            </Grid>
            {qa && (
              <div className="border border-lm-gold rounded-xl bg-[#FDF7EA] p-3 space-y-2.5" data-testid="quick-add-panel">
                <p className="text-xs font-bold text-lm-brown uppercase tracking-wider">Nuova scheda in rubrica</p>
                <Grid>
                  <Input data-testid="qa-name" placeholder="Nome (es. Conceria Rimalcuoio)" value={qa.name} onChange={(e) => setQa((q) => ({ ...q, name: e.target.value }))} className="bg-white" />
                  <Select value={qa.kind} onValueChange={(v) => setQa((q) => ({ ...q, kind: v }))}>
                    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="fornitore">Fornitore</SelectItem><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="sede">Sede</SelectItem></SelectContent>
                  </Select>
                  <Input data-testid="qa-address" placeholder="Indirizzo completo (via, civico, città)" value={qa.address} onChange={(e) => setQa((q) => ({ ...q, address: e.target.value }))} className="bg-white sm:col-span-2" />
                  <Input placeholder="Lat GPS (opz.)" value={qa.lat} onChange={(e) => setQa((q) => ({ ...q, lat: e.target.value }))} className="bg-white" />
                  <Input placeholder="Lng GPS (opz.)" value={qa.lng} onChange={(e) => setQa((q) => ({ ...q, lng: e.target.value }))} className="bg-white" />
                  <Input placeholder="Referente" value={qa.contact_name} onChange={(e) => setQa((q) => ({ ...q, contact_name: e.target.value }))} className="bg-white" />
                  <Input placeholder="Telefono" value={qa.contact_phone} onChange={(e) => setQa((q) => ({ ...q, contact_phone: e.target.value }))} className="bg-white" />
                </Grid>
                <p className="text-[11px] text-lm-muted">Le coordinate GPS sono facoltative ma consigliate: abilitano mappa e calcolo automatico dei percorsi.</p>
                <div className="flex gap-2">
                  <Button size="sm" data-testid="qa-save" onClick={saveQuick} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva in rubrica</Button>
                  <Button size="sm" variant="outline" onClick={() => setQa(null)} className="border-lm-sand text-lm-brown">Annulla</Button>
                </div>
              </div>
            )}
            <div className="rounded-xl bg-white border border-lm-sandsoft p-3 text-sm" data-testid="address-summary">
              <p className="flex items-center gap-1.5 text-lm-text"><MapPin className="w-4 h-4 text-lm-sand shrink-0" />{(editing.operation_type === "consegna" ? editing.dest_address : editing.origin_address) || "Indirizzo dalla rubrica — scegli fornitore e sede"}</p>
              {(editing.contact_name || editing.contact_phone) && <p className="text-xs text-lm-muted mt-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{editing.contact_name} {editing.contact_phone}</p>}
              <p className="text-[11px] text-lm-muted mt-1">Indirizzo e contatti si compilano dalla rubrica. Per correggerli apri i "Dettagli aggiuntivi".</p>
            </div>
          </Section>

          <Section title="Quando">
            <Field label="Da ritirare / consegnare entro" hint="L'unica data necessaria: entro quando l'operazione deve essere completata.">
              <Input data-testid="f-deadline" type="datetime-local" value={editing.deadline} onChange={(e) => set("deadline", e.target.value)} className="bg-white" /></Field>
          </Section>

          <Section title="Merce">
            <Field label="Tipo di merce" hint="Cosa va trasportato: tipo di articoli, materiale, quantità indicativa.">
              <Textarea data-testid="f-goods" value={editing.goods_description || ""} onChange={(e) => set("goods_description", e.target.value)} className="bg-white" rows={2} placeholder="Es. 20 pelli conciate al vegetale su bancale" /></Field>
            <Field label="Mezzo consigliato per il ritiro/consegna" hint="Mezzo suggerito per l'operazione. In fase di pianificazione verrà verificata la reale compatibilità (es. merce da tenere al coperto non va sul cassonato scoperto).">
              <Select value={editing.vehicle_required || "__none"} onValueChange={(v) => set("vehicle_required", v === "__none" ? "" : v)}>
                <SelectTrigger data-testid="f-vehicle" className="bg-white"><SelectValue placeholder="Seleziona il mezzo consigliato" /></SelectTrigger>
                <SelectContent><SelectItem value="__none">Non specificato</SelectItem>{VEHICLE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note operative (ben visibili all'autista)" hint="Indicazioni pratiche per l'autista: accessi, citofono, precauzioni, contatti alternativi.">
              <Textarea data-testid="f-notes" value={editing.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-white" rows={2} placeholder="Es. Ingresso carico sul retro, suonare al magazzino" /></Field>
          </Section>

          <div className="border-t border-lm-sandsoft pt-4">
            <button type="button" data-testid="toggle-advanced" onClick={() => setShowAdv((s) => !s)}
              className="flex items-center gap-2 text-sm font-semibold text-lm-brown hover:text-lm-brownh">
              <SlidersHorizontal className="w-4 h-4" />
              Dettagli aggiuntivi (facoltativi — utili alla pianificazione)
              {showAdv ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <p className="text-[11px] text-lm-muted mt-1">Compila solo se servono: peso e mezzo aiutano il calcolo automatico dei giri; l'appuntamento serve se c'è un orario fisso.</p>

            {showAdv && (
              <div className="mt-4 space-y-5">
                <Section title="Tempistiche avanzate">
                  <Grid>
                    <Field label="Priorità" hint="Urgenza: incide sull'ordine di pianificazione.">
                      <Select value={editing.priority} onValueChange={(v) => set("priority", v)}>
                        <SelectTrigger data-testid="f-priority" className="bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(PRIORITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label="Merce disponibile da" hint="Data e ora a partire dalle quali la merce è pronta.">
                      <Input type="datetime-local" value={editing.available_from} onChange={(e) => set("available_from", e.target.value)} className="bg-white" /></Field>
                    <Field label="Appuntamento tassativo (opz.)" hint="Solo se c'è un orario fisso concordato da rispettare.">
                      <Input type="datetime-local" value={editing.appointment} onChange={(e) => set("appointment", e.target.value)} className="bg-white" /></Field>
                    <Field label="Orari apertura / pause" hint="Fasce orarie in cui è possibile presentarsi, incluse le pause.">
                      <Input value={editing.opening_hours || ""} onChange={(e) => set("opening_hours", e.target.value)} placeholder="Es. 8-12 / 14-18" className="bg-white" /></Field>
                  </Grid>
                  <label className="flex items-center gap-3 mt-1 bg-white border border-lm-sandsoft rounded-lg px-3 py-2.5">
                    <Switch data-testid="f-strict" checked={editing.deadline_strict} onCheckedChange={(v) => set("deadline_strict", v)} />
                    <span className="text-sm text-lm-text">Scadenza <b>inderogabile</b> (non solo preferita)</span>
                  </label>
                </Section>

                <Section title="Indirizzi e contatti">
                  <Grid>
                    <Field label="Indirizzo di partenza (ritiro)">
                      <Input data-testid="f-origin" value={editing.origin_address || ""} onChange={(e) => set("origin_address", e.target.value)} className="bg-white" placeholder="Via, civico, città" /></Field>
                    <Field label="Indirizzo di destinazione (consegna)">
                      <Input data-testid="f-dest" value={editing.dest_address || ""} onChange={(e) => set("dest_address", e.target.value)} className="bg-white" placeholder="Via, civico, città" /></Field>
                    <Field label="Referente sul posto">
                      <Input value={editing.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="bg-white" placeholder="Es. Sig. Fumagalli" /></Field>
                    <Field label="Telefono referente">
                      <Input value={editing.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="bg-white" placeholder="Es. +39 031 700100" /></Field>
                  </Grid>
                </Section>

                <Section title="Dettagli merce e mezzo">
                  <Grid>
                    <Field label="N. colli" hint="Numero totale di colli/pacchi da caricare, se noto.">
                      <Input type="number" value={editing.packages_count} onChange={(e) => set("packages_count", e.target.value)} className="bg-white" placeholder="Es. 4" /></Field>
                    <Field label="Peso (kg)" hint="Peso complessivo stimato: serve a verificare la portata del mezzo.">
                      <Input type="number" value={editing.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="bg-white" placeholder="Es. 180" /></Field>
                    <Field label="Volume (m³)" hint="Ingombro in metri cubi: serve a verificare la capacità del vano.">
                      <Input type="number" step="0.1" value={editing.volume_m3} onChange={(e) => set("volume_m3", e.target.value)} className="bg-white" placeholder="Es. 1.2" /></Field>
                    <Field label="Dimensioni / ingombro" hint="Misure indicative dei colli più grandi (L × L × H).">
                      <Input value={editing.dimensions || ""} onChange={(e) => set("dimensions", e.target.value)} className="bg-white" placeholder="Es. 120×80×100 cm" /></Field>
                  </Grid>
                  <Field label="Necessità particolari" hint="Seleziona tutte le esigenze: influenzano la scelta del mezzo e delle dotazioni.">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(NEEDS).map(([k, v]) => (
                        <label key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${editing.needs.includes(k) ? "bg-lm-brown text-lm-cream border-lm-brown" : "bg-white border-lm-sandsoft text-lm-text2"}`}>
                          <Checkbox checked={editing.needs.includes(k)} onCheckedChange={() => toggleNeed(k)} className="border-lm-sand data-[state=checked]:bg-lm-cream data-[state=checked]:text-lm-brown" />
                          {v}
                        </label>
                      ))}
                    </div>
                  </Field>
                </Section>

                <Section title="Collegamenti">
                  <Field label="Collega a un'altra richiesta (ritiro↔consegna)" hint="Collega un ritiro alla relativa consegna: il sistema garantirà l'ordine (prima si ritira, poi si consegna).">
                    <Select value={editing.linked_request_id || "__none"} onValueChange={(v) => set("linked_request_id", v === "__none" ? "" : v)}>
                      <SelectTrigger className="bg-white"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Nessun collegamento</SelectItem>
                        {rows.filter((r) => r.id !== editing.id).map((r) => <SelectItem key={r.id} value={r.id}>{r.code} — {OPERATION_TYPES[r.operation_type]} {r.supplier_name || ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </Section>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-lm-sand text-lm-brown">Annulla</Button>
          <Button data-testid="save-request-btn" onClick={handleSave} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva richiesta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ title, children }) => (
  <div><p className="text-xs uppercase tracking-wider text-lm-muted font-bold mb-2">{title}</p><div className="space-y-3">{children}</div></div>
);
const Grid = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-lm-text2 text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] text-lm-muted mt-1 leading-snug">{hint}</p>}
  </div>
);
